import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { locationHealthAPI } from '../lib/api';
import { toast } from 'sonner';
import {
    Star,
    HelpCircle,
    Loader2,
    CheckCircle,
    AlertTriangle,
    Copy,
    FileSpreadsheet,
    Save,
    X,
    Info
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '../components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../components/ui/tooltip';
import { InfoBanner } from './UsabilityComponents';

// Help tooltip component
const HelpTooltip = ({ text }) => (
    <TooltipProvider>
        <Tooltip>
            <TooltipTrigger asChild>
                <HelpCircle className="w-3 h-3 text-gray-400 cursor-help inline-block ml-1" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs z-[100]">
                <p className="text-xs">{text}</p>
            </TooltipContent>
        </Tooltip>
    </TooltipProvider>
);

export default function QuickSetBaselinesModal({ open, onOpenChange, onSuccess }) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [locations, setLocations] = useState([]);
    const [formData, setFormData] = useState({});
    const [baselineDate, setBaselineDate] = useState(new Date().toISOString().split('T')[0]);

    useEffect(() => {
        if (open) {
            loadLocationsWithoutBaseline();
        }
    }, [open]);

    const loadLocationsWithoutBaseline = async () => {
        setLoading(true);
        try {
            const res = await locationHealthAPI.getLocationsWithoutBaseline();
            setLocations(res.data.locations || []);
            
            // Initialize form data with empty values
            const initialData = {};
            (res.data.locations || []).forEach(loc => {
                initialData[loc.id] = {
                    google_reviews: '',
                    google_rating: '',
                    facebook_likes: '',
                    instagram_followers: '',
                    yelp_reviews: '',
                    yelp_rating: '',
                    trustpilot_reviews: '',
                    trustpilot_rating: ''
                };
            });
            setFormData(initialData);
        } catch (error) {
            toast.error('Failed to load locations');
        } finally {
            setLoading(false);
        }
    };

    const updateField = (locationId, field, value) => {
        setFormData(prev => ({
            ...prev,
            [locationId]: {
                ...prev[locationId],
                [field]: value
            }
        }));
    };

    const copyFromAbove = (locationId, currentIndex) => {
        if (currentIndex === 0) return;
        
        const prevLocation = locations[currentIndex - 1];
        const prevData = formData[prevLocation.id];
        
        if (prevData) {
            setFormData(prev => ({
                ...prev,
                [locationId]: { ...prevData }
            }));
            toast.success('Values copied from above');
        }
    };

    const hasAnyData = (data) => {
        return Object.values(data).some(v => v !== '' && v !== null && v !== undefined);
    };

    const handleSaveAll = async () => {
        // Build entries for locations with data
        const entries = [];
        
        Object.entries(formData).forEach(([locationId, data]) => {
            if (hasAnyData(data)) {
                const entry = { location_id: locationId, baseline_date: baselineDate };
                
                // Only include non-empty values
                if (data.google_reviews !== '') entry.google_reviews = parseInt(data.google_reviews) || null;
                if (data.google_rating !== '') entry.google_rating = parseFloat(data.google_rating) || null;
                if (data.facebook_likes !== '') entry.facebook_likes = parseInt(data.facebook_likes) || null;
                if (data.instagram_followers !== '') entry.instagram_followers = parseInt(data.instagram_followers) || null;
                if (data.yelp_reviews !== '') entry.yelp_reviews = parseInt(data.yelp_reviews) || null;
                if (data.yelp_rating !== '') entry.yelp_rating = parseFloat(data.yelp_rating) || null;
                if (data.trustpilot_reviews !== '') entry.trustpilot_reviews = parseInt(data.trustpilot_reviews) || null;
                if (data.trustpilot_rating !== '') entry.trustpilot_rating = parseFloat(data.trustpilot_rating) || null;
                
                entries.push(entry);
            }
        });

        if (entries.length === 0) {
            toast.error('Please enter baseline values for at least one location');
            return;
        }

        setSaving(true);
        try {
            const res = await locationHealthAPI.bulkUpdateBaseline(entries);
            toast.success(res.data.message || `Baselines set for ${res.data.success_count} locations`);
            
            if (res.data.failed_count > 0) {
                toast.warning(`${res.data.failed_count} locations failed to update`);
            }
            
            onSuccess?.();
            onOpenChange(false);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save baselines');
        } finally {
            setSaving(false);
        }
    };

    const locationsWithData = Object.entries(formData).filter(([_, data]) => hasAnyData(data)).length;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-[95vw] max-h-[90vh] overflow-hidden flex flex-col">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Star className="w-5 h-5 text-amber-500" />
                        Quick Set Baselines
                    </DialogTitle>
                    <DialogDescription>
                        Enter the starting values for your locations <strong>before you started using YulOnTap</strong>. 
                        This helps calculate your actual ROI by tracking only the <strong>new reviews</strong> generated by your Tap Engine Cards.
                    </DialogDescription>
                </DialogHeader>

                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
                    </div>
                ) : locations.length === 0 ? (
                    <div className="text-center py-12">
                        <CheckCircle className="w-16 h-16 mx-auto mb-4 text-emerald-300" />
                        <h3 className="text-lg font-semibold text-foreground mb-2">All Caught Up!</h3>
                        <p className="text-muted-foreground">All your locations already have baseline values set.</p>
                    </div>
                ) : (
                    <>
                        {/* How it works tip */}
                        <InfoBanner type="tip" title="How does this work?">
                            Enter each location&apos;s review count and rating as of the baseline date. 
                            Only fill in platforms the location actually uses. Leave others blank.
                        </InfoBanner>

                        {/* Baseline Date */}
                        <div className="flex items-center gap-4 p-3 bg-blue-50 rounded-lg border border-blue-200 my-3">
                            <Label className="text-sm font-medium text-blue-700 whitespace-nowrap flex items-center gap-1">
                                Baseline Date for All:
                                <HelpTooltip text="The date you started using YulOnTap. All locations will use this date as their starting point." />
                            </Label>
                            <Input
                                type="date"
                                value={baselineDate}
                                onChange={(e) => setBaselineDate(e.target.value)}
                                className="w-40 bg-background"
                            />
                            <HelpTooltip text="This date will be applied to all locations. Usually today for new onboarding." />
                            <div className="flex-1" />
                            <Badge variant="outline" className="bg-background">
                                {locations.length} locations without baselines
                            </Badge>
                        </div>

                        {/* Table */}
                        <div className="flex-1 overflow-auto border rounded-lg">
                            <Table>
                                <TableHeader className="sticky top-0 bg-muted z-10">
                                    <TableRow>
                                        <TableHead className="min-w-[180px] sticky left-0 bg-muted z-20">
                                            Location
                                        </TableHead>
                                        <TableHead className="min-w-[90px] text-center">
                                            <span className="text-blue-600">G</span> Reviews
                                        </TableHead>
                                        <TableHead className="min-w-[80px] text-center">
                                            <span className="text-blue-600">G</span> Rating
                                        </TableHead>
                                        <TableHead className="min-w-[90px] text-center">
                                            <span className="text-indigo-600">FB</span> Likes
                                        </TableHead>
                                        <TableHead className="min-w-[90px] text-center">
                                            <span className="text-pink-600">IG</span> Followers
                                        </TableHead>
                                        <TableHead className="min-w-[90px] text-center">
                                            <span className="text-red-600">Y</span> Reviews
                                        </TableHead>
                                        <TableHead className="min-w-[80px] text-center">
                                            <span className="text-red-600">Y</span> Rating
                                        </TableHead>
                                        <TableHead className="min-w-[90px] text-center">
                                            <span className="text-green-600">TP</span> Reviews
                                        </TableHead>
                                        <TableHead className="min-w-[80px] text-center">
                                            <span className="text-green-600">TP</span> Rating
                                        </TableHead>
                                        <TableHead className="w-[50px]"></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {locations.map((loc, index) => (
                                        <TableRow key={loc.id} className={hasAnyData(formData[loc.id] || {}) ? 'bg-emerald-50' : ''}>
                                            <TableCell className="font-medium sticky left-0 bg-card z-10">
                                                <div>
                                                    <p className="text-sm font-medium text-foreground">{loc.name}</p>
                                                    <p className="text-xs text-muted-foreground">{loc.organization_name}</p>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    placeholder="0"
                                                    className="w-20 h-8 text-sm text-center"
                                                    value={formData[loc.id]?.google_reviews || ''}
                                                    onChange={(e) => updateField(loc.id, 'google_reviews', e.target.value)}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Input
                                                    type="number"
                                                    min="1"
                                                    max="5"
                                                    step="0.1"
                                                    placeholder="4.5"
                                                    className="w-16 h-8 text-sm text-center"
                                                    value={formData[loc.id]?.google_rating || ''}
                                                    onChange={(e) => updateField(loc.id, 'google_rating', e.target.value)}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    placeholder="0"
                                                    className="w-20 h-8 text-sm text-center"
                                                    value={formData[loc.id]?.facebook_likes || ''}
                                                    onChange={(e) => updateField(loc.id, 'facebook_likes', e.target.value)}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    placeholder="0"
                                                    className="w-20 h-8 text-sm text-center"
                                                    value={formData[loc.id]?.instagram_followers || ''}
                                                    onChange={(e) => updateField(loc.id, 'instagram_followers', e.target.value)}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    placeholder="0"
                                                    className="w-20 h-8 text-sm text-center"
                                                    value={formData[loc.id]?.yelp_reviews || ''}
                                                    onChange={(e) => updateField(loc.id, 'yelp_reviews', e.target.value)}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Input
                                                    type="number"
                                                    min="1"
                                                    max="5"
                                                    step="0.1"
                                                    placeholder="4.5"
                                                    className="w-16 h-8 text-sm text-center"
                                                    value={formData[loc.id]?.yelp_rating || ''}
                                                    onChange={(e) => updateField(loc.id, 'yelp_rating', e.target.value)}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    placeholder="0"
                                                    className="w-20 h-8 text-sm text-center"
                                                    value={formData[loc.id]?.trustpilot_reviews || ''}
                                                    onChange={(e) => updateField(loc.id, 'trustpilot_reviews', e.target.value)}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Input
                                                    type="number"
                                                    min="1"
                                                    max="5"
                                                    step="0.1"
                                                    placeholder="4.5"
                                                    className="w-16 h-8 text-sm text-center"
                                                    value={formData[loc.id]?.trustpilot_rating || ''}
                                                    onChange={(e) => updateField(loc.id, 'trustpilot_rating', e.target.value)}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {index > 0 && (
                                                    <TooltipProvider>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    className="h-7 w-7 p-0"
                                                                    onClick={() => copyFromAbove(loc.id, index)}
                                                                >
                                                                    <Copy className="w-3 h-3" />
                                                                </Button>
                                                            </TooltipTrigger>
                                                            <TooltipContent>
                                                                <p className="text-xs">{t('quickSetBaselines.copyValues')}</p>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>

                        {/* Help text */}
                        <div className="flex items-start gap-2 p-3 bg-muted rounded-lg text-sm text-muted-foreground mt-4">
                            <HelpCircle className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                            <div>
                                <p><strong>Tip:</strong> Press <kbd className="px-1 py-0.5 bg-slate-200 rounded text-xs">Tab</kbd> to move between fields quickly.</p>
                                <p className="mt-1">Only locations with at least one value will be saved. Empty rows are skipped.</p>
                            </div>
                        </div>
                    </>
                )}

                <DialogFooter className="mt-4">
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
                        Cancel
                    </Button>
                    {locations.length > 0 && (
                        <Button 
                            onClick={handleSaveAll} 
                            disabled={saving || locationsWithData === 0}
                            className="min-w-[120px]"
                        >
                            {saving ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : (
                                <Save className="w-4 h-4 mr-2" />
                            )}
                            Save All ({locationsWithData})
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
